import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Users, Search, Eye, Save, Euro, Calendar, Mail, Phone, FileText, Loader2, BookOpen } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { API } from "@/App";

const CustomersManager = ({ role = "business", businessId = null, businesses = [] }) => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBusiness, setSelectedBusiness] = useState(businessId || "all");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [customerNotes, setCustomerNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, [role, businessId, selectedBusiness]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      let endpoint = `${API}/my-business/customers`;
      
      if (role === "admin" && businessId) {
        endpoint = `${API}/admin/businesses/${businessId}/customers`;
      } else if (role === "superadmin") {
        endpoint = selectedBusiness !== "all" 
          ? `${API}/superadmin/customers?business_id=${selectedBusiness}`
          : `${API}/superadmin/customers`;
      }
      
      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(res.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Errore nel caricamento clienti");
    } finally {
      setLoading(false);
    }
  };

  const handleViewCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setCustomerNotes(customer.notes || "");
    
    // Fetch full details with booking history
    try {
      const token = localStorage.getItem("token");
      let endpoint = `${API}/my-business/customers/${customer.customer_id}`;
      
      if (role === "admin" && businessId) {
        // Admin uses same endpoint structure
        endpoint = `${API}/admin/businesses/${customer.business_id}/customers/${customer.customer_id}`;
      }
      
      // For business owner, fetch with history
      if (role === "business") {
        const res = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSelectedCustomer(res.data);
      }
    } catch (error) {
      console.log("Could not fetch full details:", error);
    }
    
    setDetailDialogOpen(true);
  };

  const handleSaveNotes = async () => {
    if (!selectedCustomer) return;
    
    setSavingNotes(true);
    try {
      const token = localStorage.getItem("token");
      let endpoint = `${API}/my-business/customers/${selectedCustomer.customer_id}`;
      
      await axios.put(endpoint, { notes: customerNotes }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Note salvate");
      // Update local state
      setSelectedCustomer({ ...selectedCustomer, notes: customerNotes });
      setCustomers(customers.map(c => 
        c.customer_id === selectedCustomer.customer_id 
          ? { ...c, notes: customerNotes } 
          : c
      ));
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSavingNotes(false);
    }
  };

  const filteredCustomers = customers.filter(c => {
    const term = searchTerm.toLowerCase();
    return c.name?.toLowerCase().includes(term) || 
           c.email?.toLowerCase().includes(term) ||
           c.phone?.includes(term);
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(parseISO(dateStr.replace('Z', '')), "dd/MM/yyyy", { locale: it });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-slate-500">Caricamento clienti...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <CardTitle>Clienti</CardTitle>
              <CardDescription>
                {customers.length} clienti totali
              </CardDescription>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2">
            {role === "superadmin" && businesses.length > 0 && (
              <Select value={selectedBusiness} onValueChange={setSelectedBusiness}>
                <SelectTrigger className="w-[200px]" data-testid="filter-business">
                  <SelectValue placeholder="Filtra per azienda" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le aziende</SelectItem>
                  {businesses.map(b => (
                    <SelectItem key={b.business_id} value={b.business_id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Cerca cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-[200px]"
                data-testid="search-customer"
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredCustomers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">
              {searchTerm ? "Nessun cliente trovato" : "Nessun cliente registrato"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Contatti</TableHead>
                  {role === "superadmin" && <TableHead>Azienda</TableHead>}
                  <TableHead className="text-center">Prenotazioni</TableHead>
                  <TableHead className="text-right">Totale Speso</TableHead>
                  <TableHead>Ultima Prenotazione</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.map((customer) => (
                  <TableRow key={customer.customer_id} data-testid={`customer-row-${customer.customer_id}`}>
                    <TableCell>
                      <div className="font-medium">{customer.name}</div>
                      {customer.notes && (
                        <Badge variant="outline" className="mt-1 text-xs">
                          <FileText className="h-3 w-3 mr-1" />Note
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm space-y-1">
                        <div className="flex items-center gap-1 text-slate-600">
                          <Mail className="h-3 w-3" />
                          {customer.email}
                        </div>
                        {customer.phone && (
                          <div className="flex items-center gap-1 text-slate-500">
                            <Phone className="h-3 w-3" />
                            {customer.phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    {role === "superadmin" && (
                      <TableCell>
                        <span className="text-sm text-slate-600">{customer.business_name}</span>
                      </TableCell>
                    )}
                    <TableCell className="text-center">
                      <Badge variant="secondary">{customer.total_bookings}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(customer.total_spent)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <Calendar className="h-3 w-3" />
                        {formatDate(customer.last_booking_date)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewCustomer(customer)}
                        data-testid={`view-customer-${customer.customer_id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Customer Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettagli Cliente</DialogTitle>
            <DialogDescription>
              Informazioni e storico prenotazioni
            </DialogDescription>
          </DialogHeader>
          
          {selectedCustomer && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-xs text-slate-500">Nome</Label>
                  <p className="font-medium">{selectedCustomer.name}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <p className="font-medium">{selectedCustomer.email}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Telefono</Label>
                  <p className="font-medium">{selectedCustomer.phone || "-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Cliente dal</Label>
                  <p className="font-medium">{formatDate(selectedCustomer.created_at)}</p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{selectedCustomer.total_bookings}</p>
                  <p className="text-sm text-slate-600">Prenotazioni</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedCustomer.total_spent)}</p>
                  <p className="text-sm text-slate-600">Totale Speso</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">
                    {selectedCustomer.total_bookings > 0 
                      ? formatCurrency(selectedCustomer.total_spent / selectedCustomer.total_bookings)
                      : "€0"}
                  </p>
                  <p className="text-sm text-slate-600">Media Ordine</p>
                </div>
              </div>

              {/* Notes */}
              {role === "business" && (
                <div className="space-y-2">
                  <Label>Note sul cliente</Label>
                  <Textarea
                    value={customerNotes}
                    onChange={(e) => setCustomerNotes(e.target.value)}
                    placeholder="Aggiungi note su questo cliente..."
                    className="min-h-[80px]"
                    data-testid="customer-notes"
                  />
                  <Button 
                    onClick={handleSaveNotes} 
                    disabled={savingNotes}
                    size="sm"
                    data-testid="save-customer-notes"
                  >
                    {savingNotes ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Salva Note
                  </Button>
                </div>
              )}

              {/* Booking History */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Storico Prenotazioni
                </Label>
                {selectedCustomer.booking_history && selectedCustomer.booking_history.length > 0 ? (
                  <div className="max-h-[250px] overflow-y-auto space-y-2">
                    {selectedCustomer.booking_history.map((booking) => (
                      <div 
                        key={booking.booking_id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border"
                      >
                        <div>
                          <p className="font-medium text-sm">
                            {formatDate(booking.start_datetime)}
                          </p>
                          <p className="text-xs text-slate-500">
                            {booking.services?.map(s => s.name).join(", ") || 
                             booking.services_details?.map(s => s.name).join(", ") || 
                             "Servizio"}
                          </p>
                          {booking.staff_name && (
                            <p className="text-xs text-slate-400">
                              Operatore: {booking.staff_name}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <Badge 
                            className={
                              booking.status === "confirmed" ? "bg-green-100 text-green-700" :
                              booking.status === "pending" ? "bg-amber-100 text-amber-700" :
                              booking.status === "cancelled" ? "bg-red-100 text-red-700" :
                              booking.status === "completed" ? "bg-blue-100 text-blue-700" :
                              "bg-slate-100 text-slate-700"
                            }
                          >
                            {booking.status === "confirmed" ? "Confermato" :
                             booking.status === "pending" ? "In attesa" :
                             booking.status === "cancelled" ? "Annullato" :
                             booking.status === "completed" ? "Completato" :
                             booking.status}
                          </Badge>
                          {(booking.total_price || booking.payment_amount) && (
                            <p className="text-sm font-medium mt-1">
                              {formatCurrency(booking.total_price || booking.payment_amount)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-slate-400 bg-slate-50 rounded-lg">
                    <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nessuna prenotazione trovata</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setDetailDialogOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CustomersManager;
